import { useState } from 'react'
import { displayedOddsValue, type OddsFormat } from '../utils/odds'

type OddsDirection = 'increased' | 'decreased' | null

interface OddsDirectionFlash {
  flashKey: number
  direction: OddsDirection
  clearFlash: () => void
}

interface OddsFlashState {
  displayValue: string | null | undefined
  lastLiveValue: string | null | undefined
  isLivePrice: boolean
  format: OddsFormat | undefined
  token: number
  direction: OddsDirection
}

function movementDirection(previous: string | null | undefined, current: string | null | undefined): OddsDirection {
  const previousOdds = displayedOddsValue(previous)
  const currentOdds = displayedOddsValue(current)
  if (previousOdds == null || currentOdds == null || previousOdds === currentOdds) return null
  return currentOdds > previousOdds ? 'increased' : 'decreased'
}

function sameOdds(left: string | null | undefined, right: string | null | undefined): boolean {
  const leftOdds = displayedOddsValue(left)
  const rightOdds = displayedOddsValue(right)
  return leftOdds != null && rightOdds != null && leftOdds === rightOdds
}

// CSS owns the fade timing. The token changes only with the rendered odds,
// remounting the flashed node so a later price movement can animate again.
// `format` is the odds format the value is shown in: when the player switches
// it, the same price re-renders in the other format, which is no move.
export function useOddsDirectionFlash(
  value: string | null | undefined,
  isLivePrice: boolean,
  format?: OddsFormat,
): OddsDirectionFlash {
  const [flash, setFlash] = useState<OddsFlashState>(() => ({
    displayValue: value,
    lastLiveValue: isLivePrice ? value : null,
    isLivePrice,
    format,
    token: 0,
    direction: null,
  }))
  const clearFlash = () => {
    setFlash((current) => current.direction ? { ...current, direction: null } : current)
  }

  if (format !== flash.format) {
    setFlash({
      displayValue: value,
      lastLiveValue: isLivePrice ? value : null,
      isLivePrice,
      format,
      token: flash.token,
      direction: null,
    })
    return { flashKey: flash.token, direction: null, clearFlash }
  }

  if (value !== flash.displayValue || isLivePrice !== flash.isLivePrice) {
    let lastLiveValue = flash.lastLiveValue
    let direction: OddsDirection = null
    if (isLivePrice) {
      const isRecovery = !flash.isLivePrice && flash.lastLiveValue != null
      if (isRecovery) {
        const matchesLastLive = sameOdds(value, flash.lastLiveValue)
        const matchesFallback = sameOdds(value, flash.displayValue)
        if (!matchesLastLive && !matchesFallback) {
          direction = movementDirection(flash.displayValue, value)
        }
      } else {
        direction = movementDirection(flash.lastLiveValue ?? flash.displayValue, value)
      }
      lastLiveValue = value
    } else if (!flash.isLivePrice) {
      lastLiveValue = null
    }

    const token = direction ? flash.token + 1 : flash.token
    setFlash({
      displayValue: value,
      lastLiveValue,
      isLivePrice,
      format,
      token,
      direction,
    })
    return { flashKey: token, direction, clearFlash }
  }

  return { flashKey: flash.token, direction: flash.direction, clearFlash }
}
